/**
 * Firestore access for CMM workflow notes under
 * `/cmm_workflow/{identifier}/notes_and_history`.
 */

import { FirestoreService } from "../../../api/firebase/firestoreService";
import { FirestoreCollectionReference } from "../../../api/firebase/references";
import { logError } from "../../../utils/customLogger";

/**
 * Optional: pin all reads/writes to one `cmm_workflow` parent document (e.g. local QA).
 * Example: `"edfb57e5-29c5-4815-a7e4-a084592fed1a"`.
 * Leave empty to use the table row’s order id (`row.id`) per order.
 */
const CMM_NOTES_WORKFLOW_PARENT_OVERRIDE = "";

/**
 * Resolves which `cmm_workflow/{id}` document backs `notes_and_history`.
 *
 * @param identifier - Order / workflow id from the UI row.
 * @returns Parent document id to use in the Firestore path.
 */
function resolveWorkflowParentId(identifier: string): string {
  const override = CMM_NOTES_WORKFLOW_PARENT_OVERRIDE.trim();
  return override || identifier;
}

/**
 * One document in `notes_and_history` (string fields as stored in Firestore).
 */
export interface CmmNotesAndHistoryDoc {
  id?: string;
  /** Note body text. */
  notes: string;
  /** Point-of-contact label: signed-in user display name (profile or email/id fallback). */
  poc: string;
  timestamp: string;
}

/**
 * Persists a new note under the workflow document’s `notes_and_history` subcollection.
 *
 * @param identifier - `cmm_workflow` parent document id (use the order / workflow id from the table row).
 * @param payload - `notes` body; `poc` is the signed-in user’s display name (profile name, else email/id); `timestamp` is set as an ISO string.
 * @returns Saved document including auto-generated `id`.
 * @throws Error if `identifier` is missing or the write fails.
 */
export const saveCmmNotesAndHistoryEntry = async (
  identifier: string,
  payload: Pick<CmmNotesAndHistoryDoc, "notes" | "poc">,
): Promise<CmmNotesAndHistoryDoc> => {
  if (!identifier?.trim()) {
    throw new Error("Identifier is required to save notes and history");
  }

  const parentId = resolveWorkflowParentId(identifier);
  const collectionPath = FirestoreCollectionReference.notesAndHistory(parentId);

  const data: Omit<CmmNotesAndHistoryDoc, "id"> = {
    notes: payload.notes,
    poc: payload.poc,
    timestamp: new Date().toISOString(),
  };

  const result = await FirestoreService.addDocument(collectionPath, data);
  return result as CmmNotesAndHistoryDoc;
};

/**
 * Loads all note documents for a workflow id, newest first (by parsed `timestamp`).
 *
 * @param identifier - `cmm_workflow` parent document id.
 * @returns Note rows; empty array if `identifier` is missing or on read failure.
 */
export const fetchCmmNotesAndHistory = async (
  identifier: string,
): Promise<CmmNotesAndHistoryDoc[]> => {
  if (!identifier?.trim()) {
    return [];
  }

  try {
    const parentId = resolveWorkflowParentId(identifier);
    const collectionPath =
      FirestoreCollectionReference.notesAndHistory(parentId);
    const docs =
      await FirestoreService.getAllDocuments<CmmNotesAndHistoryDoc>(
        collectionPath,
      );
    return docs.sort((a, b) => {
      const aTime = Date.parse(a.timestamp);
      const bTime = Date.parse(b.timestamp);
      const aOk = !Number.isNaN(aTime);
      const bOk = !Number.isNaN(bTime);
      if (aOk && bOk) return bTime - aTime;
      if (bOk) return 1;
      if (aOk) return -1;
      return 0;
    });
  } catch (error) {
    logError(error as Error, "Error fetching CMM notes and history");
    return [];
  }
};
